import {
  PLATFORM_ASSET_RULES,
  type PlatformAssetKey,
  type PlatformAssetMeta,
  type PlatformSettings,
  type PlatformSiteText,
} from '@inflip/contracts/admin';
import { useEffect, useRef, useState } from 'react';

import { Field, SavedAt, usePlatformSettings } from '../components/settings-form';
import { Button, ErrorBanner, LoadingRow, PageHeader, Section, inputCls } from '../components/ui';
import { api } from '../lib/api-client';
import { applyAdminFavicon } from '../lib/favicon';

/** 홈페이지 설정 — 파비콘 / OG 이미지 첨부, OG 텍스트, HEAD 스크립트 */
export function SiteSettingsPage() {
  const { session, settings, error, run } = usePlatformSettings();
  const [form, setForm] = useState<PlatformSiteText | null>(null);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<Date | null>(null);

  useEffect(() => {
    if (settings && !form) {
      const { ogTitle, ogDescription, headScript } = settings.site;
      setForm({ ogTitle, ogDescription, headScript });
    }
  }, [settings, form]);

  // 어드민 탭 아이콘도 같은 파비콘 — 올리거나 지우면 즉시 교체
  const favicon = settings?.site.favicon ?? null;
  useEffect(() => {
    if (settings) applyAdminFavicon(favicon);
  }, [settings, favicon]);

  if (!session) return null;

  const save = async () => {
    if (!form || saving) return;
    setSaving(true);
    setSavedAt(null);
    const ok = await run(() => api.updateSiteSettings(session.accessToken, form));
    if (ok) setSavedAt(new Date());
    setSaving(false);
  };

  return (
    <main className="mx-auto w-full max-w-3xl px-6 py-8">
      <PageHeader
        title="홈페이지 설정"
        description="파비콘·OG 이미지는 올리는 즉시 반영되고, 텍스트와 스크립트는 저장 버튼으로 반영돼요"
        actions={
          <>
            <SavedAt at={savedAt} />
            <Button disabled={saving || !form} onClick={() => void save()}>
              {saving ? '저장 중…' : '저장'}
            </Button>
          </>
        }
      />
      {error && <ErrorBanner message={error} />}
      {!settings && !error && <LoadingRow />}

      {settings && form && (
        <>
          <AssetSection
            assetKey="favicon"
            title="파비콘"
            subtitle="모든 로케일 공통"
            description="브라우저 탭에 표시되는 아이콘입니다. 권장 크기: 64×64 이상, PNG · JPG · WEBP · ICO 형식. 업로드한 파일은 원본 그대로 저장돼요 (압축 없음)."
            meta={settings.site.favicon}
            token={session.accessToken}
            previewCls="h-12 w-12 rounded-lg"
            onChange={run}
          />
          <AssetSection
            assetKey="ogImage"
            title="OG 이미지"
            subtitle="SNS / 문자 미리보기 · 모든 로케일 공통"
            description="카카오톡, 문자, 페이스북 등 링크 공유 시 표시되는 대표 이미지입니다. 권장 크기: 1200×630, PNG · JPG · WEBP 형식. 업로드한 파일은 원본 그대로 저장돼요 (압축 없음)."
            meta={settings.site.ogImage}
            token={session.accessToken}
            previewCls="aspect-[1200/630] w-56 rounded-lg"
            onChange={run}
          />

          <Section title="OG 텍스트">
            <div className="space-y-3">
              <Field label="OG 제목" hint="검색 결과·링크 공유 시 표시되는 제목">
                <input
                  className={`w-full ${inputCls}`}
                  value={form.ogTitle}
                  maxLength={120}
                  onChange={(e) => setForm({ ...form, ogTitle: e.target.value })}
                />
              </Field>
              <Field label="OG 설명">
                <textarea
                  className={`h-20 w-full resize-none ${inputCls}`}
                  value={form.ogDescription}
                  maxLength={300}
                  onChange={(e) => setForm({ ...form, ogDescription: e.target.value })}
                />
              </Field>
            </div>
          </Section>

          <Section title="HEAD 스크립트">
            <p className="text-xs text-muted-foreground">
              GA·픽셀 같은 추적 스크립트나 사이트 소유 확인 meta 태그를 넣어요. 모든 페이지에 공통
              적용돼요.
            </p>
            <textarea
              className={`mt-2 h-44 w-full resize-y font-mono text-xs ${inputCls}`}
              spellCheck={false}
              placeholder={'<meta name="naver-site-verification" content="…" />\n<script async src="https://www.googletagmanager.com/gtag/js?id=G-…"></script>'}
              value={form.headScript}
              maxLength={20_000}
              onChange={(e) => setForm({ ...form, headScript: e.target.value })}
            />
            <p className="mt-1 text-xs text-red-500">
              여기 입력한 코드는 검수 없이 그대로 사이트 &lt;head&gt; 에 주입돼요. 신뢰할 수 있는
              코드만 붙여 넣어 주세요.
            </p>
          </Section>
        </>
      )}
    </main>
  );
}

/** 첨부 자산 한 구역 — 미리보기(+삭제 배지) 와 변경 버튼. 선택 즉시 업로드 */
function AssetSection({
  assetKey,
  title,
  subtitle,
  description,
  meta,
  token,
  previewCls,
  onChange,
}: {
  assetKey: PlatformAssetKey;
  title: string;
  subtitle: string;
  description: string;
  meta: PlatformAssetMeta | null;
  token: string;
  previewCls: string;
  onChange: (task: () => Promise<PlatformSettings>) => Promise<boolean>;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const previewUrl = meta ? api.platformAssetUrl(assetKey, meta) : null;
  const accept = PLATFORM_ASSET_RULES[assetKey].mimeTypes.join(',');

  const upload = async (file: File | undefined) => {
    if (!file || busy) return;
    setBusy(true);
    await onChange(() => api.uploadPlatformAsset(token, assetKey, file));
    setBusy(false);
    if (inputRef.current) inputRef.current.value = '';
  };

  const remove = async () => {
    if (busy) return;
    setBusy(true);
    await onChange(() => api.deletePlatformAsset(token, assetKey));
    setBusy(false);
  };

  return (
    <Section
      title={title}
      action={<span className="text-xs text-muted-foreground">{subtitle}</span>}
    >
      <p className="text-xs text-muted-foreground">{description}</p>
      <div className="mt-3 flex items-center gap-4">
        {meta && previewUrl ? (
          <div className="relative">
            <img
              src={previewUrl}
              alt=""
              className={`${previewCls} border border-border bg-muted object-contain`}
            />
            <button
              type="button"
              aria-label={`${title} 삭제`}
              disabled={busy}
              onClick={() => void remove()}
              className="absolute -right-2 -top-2 grid h-5 w-5 place-items-center rounded-full bg-red-500 text-white shadow hover:bg-red-600 disabled:opacity-40"
            >
              <svg viewBox="0 0 12 12" className="h-3 w-3" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M3 3l6 6M9 3l-6 6" strokeLinecap="round" />
              </svg>
            </button>
          </div>
        ) : (
          <div
            className={`${previewCls} grid place-items-center border border-dashed border-border text-[11px] text-muted-foreground`}
          >
            없음
          </div>
        )}
        <div className="flex flex-col gap-1">
          <Button
            variant="outline"
            disabled={busy}
            onClick={() => inputRef.current?.click()}
            className="inline-flex items-center gap-1.5"
          >
            <svg viewBox="0 0 16 16" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="1.6">
              <path d="M8 10V3M5 6l3-3 3 3M3 11v2h10v-2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            {busy ? '처리 중…' : meta ? '변경' : '업로드'}
          </Button>
          {meta && (
            <span className="text-[11px] text-muted-foreground">
              {meta.mimeType.replace('image/', '').toUpperCase()} · {formatBytes(meta.size)}
            </span>
          )}
        </div>
        <input
          ref={inputRef}
          type="file"
          accept={accept}
          className="hidden"
          onChange={(e) => void upload(e.target.files?.[0])}
        />
      </div>
    </Section>
  );
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n}B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)}KB`;
  return `${(n / 1024 / 1024).toFixed(1)}MB`;
}
